import i18n from 'i18next';
import axios from "axios";
import { useTranslation } from "react-i18next";
import * as Yup from 'yup';
import { setKey, geocode, RequestType, } from "react-geocode";


export interface ResponseData {
    statusCode?: string,
    statusMessage?: string,
    description?: string;
    data?: any;// Ogetto generico
    time?: string;  // Formato: YYYY-MM-DD'T'HH:mm:ssxxx
}

// Vehicle: Oggetto che rappresenta un veicolo
export interface Vehicle {
  vin: string;
  plate: string;
  timestamp: string;  // Formato: data e ora del veicolo
  locationData: VehicleLocationData;
  statusData: VehicleStatusData;
  statsData: VehicleStatsData;
  imei?: string;
  obuId?: string;
}

// VehicleLocationData: Dati relativi alla posizione del veicolo
export interface VehicleLocationData {
  latitude: VehicleProperty;
  longitude: VehicleProperty;
  address?: VehicleProperty;
}


export interface VehicleProperty {
    type?: string;
    value?: string;
    timestamp?: string
}

// VehicleStatusData: Stato del veicolo
export interface VehicleStatusData {
  engine?: VehicleProperty;  // es: "LOCKED", "UNLOCKED"
  doors?: VehicleProperty;  // es: "LOCKED", "UNLOCKED"
  fuelLevel?: VehicleProperty;  // Percentuale di carburante
  chargeLevel?: VehicleProperty;  // Percentuale di batteria, se elettrico
  chargePort?: VehicleProperty;  // Stato cavo
  chargeStatus?: VehicleProperty; //Stato vehicle
  maintenanceMode?: VehicleProperty; //Stato cavo
  cable?: VehicleProperty; //Stato cavo
  ignition?: VehicleProperty;
  location?: VehicleProperty;//clone by VehicleLocationData
  distanceTraveled?: VehicleProperty;//clone by totalDistanceTraveledInKm
}

// VehicleStatsData: Statistiche del veicolo
export interface VehicleStatsData {
  totalDistanceTraveledInKm?: VehicleProperty;  // Lettura dell'odometro
}

// ListVehiclesRequest: Parametri per la lista dei veicoli
export interface ListVehiclesRequest {
  filter?: string;  // Filtro opzionale
  sort?: string;  // Ordinamento opzionale
}

// CommandRequest: Oggetto per la richiesta di comando
export interface CommandRequest {
  vehicleId: Vehicle;
  cmdId: number;  // ID del comando, es: 1 per ENGINE UNLOCK
  callId?: number;  // ID della chiamata, usato per correlare richiesta e risposta
}
// VehicleStatusRequest interface
export interface VehicleStatusRequest {
  carVin: string;
  carPlate: string;
  carBcNumber: string;
  carImei: string;
}




export class VehicleCommandActions {
    private PATH = "/operation/vehicle/vci";
    private emptyVehicle: Vehicle = vehicleCommandRepoUtil.emptyVehicle as Vehicle; 
    
    // Tipizzazione corretta del metodo delay
    private delay: (name: string, ms: number) => Promise<string> = (name, ms) => {
      return new Promise(resolve => setTimeout(() => {
        console.log(name + ':delay:lock:' + ms + '!');
        resolve('lock');  // Garantiamo che venga restituita una stringa
      }, ms)).then(() => {
        console.log(name + ':delay:unlock!');
        return 'unlock';  // Qui restituiamo una stringa
      });
    };
    
    public async vehicleStatus(request: VehicleStatusRequest): Promise<ResponseData> {
      const endpoint = `/vehicles/vehicle/${request.carVin}`;
      try {
        // Effettua la richiesta
        const resp = await this.makeRequest<ResponseData>('GET', endpoint);
        if (resp) {
          if(resp?.statusCode !== '200'){
            resp.data = this.emptyVehicle;
          } 
          //Se non mi torna l'obj statusData
          if(!(resp.data?.statusData)){
            resp.data.statusData = this.emptyVehicle.statusData;
          }
          // Formatta i timestamp e i dati
          this.formatDatas(resp, 0);
          this.formatGenericData(resp?.data);
        }
        return resp; // Restituisce la risposta elaborata
      } catch (error) {
        console.error("Errore durante la richiesta vehicleStatus:", error);
        //return this.emptyVehicle; // Gestione fallback in caso di errore
        return { statusCode: '500', statusMessage: 'Errore interno', data: this.emptyVehicle };
      }
    }
    
    
    public async fetchAddressSync(lat: string, lng: string, t:any): Promise<string> {
        // @ts-ignore
        let apiKey: (string | null) = window?.appConfig?.ENV === 'PROD'
            ? process.env.REACT_APP_GOOGLE_APY_KEY_PROD
            : process.env.REACT_APP_GOOGLE_APY_KEY_DEV;

        // Forza l'assegnazione di un apiKey
        if(localStorage.getItem("geocode_api_key")){
          apiKey = localStorage.getItem("geocode_api_key")
        }
        
        if (!apiKey) {
            throw new Error("API key non trovata");
        }

        // Usa la chiave API (se hai una funzione setKey)
        setKey(apiKey);

        try {
            // Esegui la richiesta con geocode
            const latlngResponse = await geocode(RequestType.LATLNG, `${lat},${lng}`);
            
            // Verifica la risposta e restituisci l'indirizzo
            if (latlngResponse.results?.length > 0) {
                return latlngResponse.results[0].formatted_address;
            }
            // Se nessun risultato trovato
            return t('MSG_ADDRESS_GMAP_NOT_FOUND')+` ${lat},${lng}`;
        } catch (error) {
            console.error("Errore durante la ricerca dell'indirizzo:", error);
            return t('MSG_ADDRESS_GMAP_NOT_FOUND')+` ${lat},${lng}`;
        }
    }


    public async vehicleCommand(request: CommandRequest): Promise<ResponseData> {
      const endpoint = `/syncCommand`;
      return this.makeRequest<ResponseData>('POST', endpoint, request);
    }

    public async vehicleLocation(request: CommandRequest): Promise<ResponseData> {
      const endpoint = `/syncLocation`;
      return this.makeRequest<ResponseData>('POST', endpoint, request);
    }

    public async vehicleBoxStatus(request: CommandRequest): Promise<ResponseData> {
      const endpoint = `/syncStatus`;
      return this.makeRequest<ResponseData>('POST', endpoint, request);
    }

    public async vehicleResetBoxStatus(request: CommandRequest): Promise<ResponseData> {
      const endpoint = `/resetBoxStatus`;
      return this.makeRequest<ResponseData>('POST', endpoint, request.vehicleId);
    }

    private async makeRequest<T extends ResponseData>(
      method: 'GET' | 'POST',
      endpoint: string,
      data?: any
    ): Promise<T> {
        const url = `${this.PATH}${endpoint}`;
        const esecutated = await (
          method === 'GET'
          ? axios.get(url)
          : axios.post(url, data)//+'?xRequestTimeout=10'
        ).then((response) => {
          return response.data as T;
          }
        ).catch((error) => {
            this.handleApiError(error);
            return {
              statusCode: '500',
              statusMessage: 'System error',
              description: 'Non riesco a gestire errore...',
            } as T;
          }
        );
        if (!endpoint.includes('/vehicles/vehicle/')) {
           await this.delay(endpoint, 1000);
        }
        return esecutated;
    }

    private handleApiError(error: unknown): void {
      if (axios.isAxiosError(error) && error.response) {
        console.error(`HTTP Error ${error.response.status}:`, error.response.data);
      } else if (error instanceof Error) {
        console.error('Request Error:', error.message);
      } else {
        console.error('An unexpected error occurred', error);
      }
    }

    public generateVehicleStatusDataSchema() {
      return vehicleCommandRepoUtil.generateVehicleStatusDataSchema();
    }
    // apend location prop
    public formatGenericData(vehicle: Vehicle): void {
      if (vehicle?.locationData) {
        let text = `${vehicle?.locationData?.address?.value|| 'N/A'}`
        let latlon =  `${vehicle?.locationData?.latitude?.value},${vehicle?.locationData?.longitude?.value}`;
        if(!((latlon.includes('0.0')||latlon.includes('N/A')) && !(latlon.includes('undefined')))){
          text += ` ${vehicle?.locationData?.latitude?.value},${vehicle?.locationData?.longitude?.value}`;
        }
        vehicle.statusData.location={
          'type': 'string',
          'value': text,
          'timestamp': vehicle?.locationData?.latitude?.timestamp
        } as VehicleProperty; 
      }
      if (vehicle?.statsData) {
        vehicle.statusData.distanceTraveled=vehicle?.statsData?.totalDistanceTraveledInKm as VehicleProperty;
      }

      // Specifica l'ordine desiderato delle chiavi
      const orderedKeys = [
        'doors', 'engine',
        'ignition', 'location',
        'fuelLevel','chargeLevel',
        'chargeStatus', 'chargePort',
        'distanceTraveled', 'maintenanceMode' ];

      // Crea un nuovo oggetto con le chiavi ordinate
      const orderedStatusData: { [key: string]: VehicleProperty | null } = {};

      for (const key of orderedKeys) {
        if (Object.hasOwn(vehicle.statusData, key)) {
		  const value: any = (vehicle.statusData as Record<string, any>)[key];
		  if (['maintenanceMode'].includes(key)) {
            continue;
          } else if (value === null || value === undefined) {
            orderedStatusData[key] = {
              type: "N/A",
              value: "N/A",
              timestamp: "N/A"
            } as VehicleProperty;
          } else{
            orderedStatusData[key] = value;
          }
        }
      }
      
      // Assegna l'oggetto ordinato al posto di quello originale
      vehicle.statusData = orderedStatusData;
    }
    
    public formatOutTimestamp(originalDate: string): string {
      const parsedDate = new Date(originalDate); // Crea un oggetto Date
      const parsedDateLocal = new Date(parsedDate.getTime() - (parsedDate.getTimezoneOffset() * 60000)); // Converte in fuso locale
      // Converte in formato ISO senza millisecondi e personalizza
      return parsedDateLocal.toISOString()
        .split('.')[0] // Rimuove i millisecondi
        .replace('T', ' ') // Sostituisce "T" con uno spazio
        .replace('Z', ' ');
    }
    
    public formatDatas(obj: any, level: number = 1): void {
      if (level > 3 || obj === null || typeof obj !== 'object') {
        return; // Termina se superi 3 livelli o l'oggetto non è valido
      }

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = obj[key];
          // Controlla se la proprietà è `time` o `timestamp`
          if ((key === 'time' || key === 'timestamp') && typeof value === 'string') {
            try {
            if(value !== null && 'N/A' !== value){
                 obj[key] = this.formatOutTimestamp(value);
              }
            } catch (error) {
              console.warn(`Impossibile formattare il valore per ${key}:`, value);
            }
          }
          if (['fuelLevel',
               'chargeLevel','chargePort',
               'chargeStatus','maintenanceMode'].includes(key) && typeof value === 'object') {
              if((value?.value && -1 == value.value)){
              obj[key].value='N/A';
            }
          }
          if (typeof value !== 'object' && value === null){
            obj[key]='N/A'
          }
          // Se il valore è un oggetto, scendi di un livello
          if (typeof value === 'object' && value !== null) {
            this.formatDatas(value, level + 1);
          }
        }
      }
    }

}


export const useCustomTranslation = () => {
  const languages = ['en', 'it', 'fr']; 
  
  const newTranslationsMap: Map<string, any> = new Map();
  newTranslationsMap.set('it',  {
    "title.win":"Dati del veicolo plate: {{plate}} vin: {{vin}}",
    "engine": "Stato motore",
    "doors": "Stato porte",
    "cable": "Stato cavo",
    "fuelLevel": "Livello carburante",
    "chargeLevel": "Livello di carica (SOC)",
    "ignition": "Stato accensione",
    "location": "Posizione veicolo",
    "distanceTraveled": "Chilometraggio totale",
    "chargePort": "Stato cavo",
    "chargeStatus": "Stato di carica",
    "maintenanceMode": "Modalità manutenzione",
    "bltUnlockDoors": "Sblocca porte",
    "bltLockDoors": "Blocca porte",
    "bltUnlockEngine": "Sblocca motore",
    "bltLockEngine": "Blocca motore",
    "bltRequestPosition": "Richiedi posizione",
    "bltRequestStatus": "Richiedi stato",
    "bltResetBoard": "Resetta bordo",
    "bltRefresh": "Aggiorna",
    "bltClose": "Chiudi",
    "DIA_CONFIRM_OK": "Si",
    "DIA_CONFIRM_TITLE": "Attenzione!",
    "DIA_CONFIRM_CANCEL": "Annulla",
    "DIA_CONFIRM_MSG": "Sei sicuro di proseguire con il comando \"{{param}}\"?",
    "MSG_OK": "Operazione completata con successo",
    "MSG_COMMAND_NOT_EXECUTED": "Comando non eseguito",
    "MSG_INVALID_TIMEOUT": "La richiesta è scaduta a causa di parametri non validi",
    "MSG_INVALID_CALLBACK_URL": "L'URL di callback fornito non è valido",
    "MSG_VEHICLE_NOT_FOUND": "Il veicolo specificato non è stato trovato",
    "MSG_VEHICLE_BUSY": "Il veicolo è attualmente in uso e non può elaborare la richiesta",
    "MSG_NOT_CONFIGURED": "Il sistema o il veicolo non è configurato correttamente",
    "MSG_TIMEOUT_EXPIRED": "L'operazione non è stata completata poiché il periodo di timeout è scaduto",
    "MSG_TRANSMISSION_FAILED": "La trasmissione dei dati è fallita durante l'operazione",
    "MSG_SYS_ERROR": "Si è verificato un errore di sistema durante l'elaborazione della richiesta",
    "MSG_SYS_ERROR_COMUNICATION": "Si è verificato un errore critico di comunicazione",
    "MSG_NOT_FOUND": "La risorsa o endpoint richiesto non è stato trovato",
    "MSG_ND": "La risorsa non è stata trovata",
    "MSG_ADDRESS_GMAP_NOT_FOUND": "Indirizzo sconosciuto",
    
  });
    
  newTranslationsMap.set('en', {
    "title.win": "Vehicle data plate: {{plate}} vin: {{vin}}",
    "engines": "Engine status",
    "doors": "Doors status",
    "cable": "Cable status",
    "fuelLevel": "Fuel level",
    "chargeLevel": "Charge level (SOC)",
    "ignition": "Ignition status",
    "location": "Vehicle location",
    "distanceTraveled": "Total mileage",
    "chargePort": "Cable status",
    "chargeStatus": "Charging status",
    "maintenanceMode": "Maintenance mode",
    "bltUnlockDoors": "Unlock doors",
    "bltLockDoors": "Lock doors",
    "bltUnlockEngine": "Unlock engine",
    "bltLockEngine": "Lock engine",
    "bltRequestPosition": "Request position",
    "bltRequestStatus": "Request status",
    "bltResetBoard": "Reset on-board",
    "bltRefresh": "Refresh",
    "bltClose": "Close",
    "DIA_CONFIRM_OK": "Yes",
    "DIA_CONFIRM_TITLE": "Warning!",
    "DIA_CONFIRM_CANCEL": "Cancel",
    "DIA_CONFIRM_MSG": "Are you sure you want to proceed with the command \"{{param}}\"?",
    "MSG_OK": "Operation completed successfully",
    "MSG_COMMAND_NOT_EXECUTED": "Command not executed",
    "MSG_INVALID_TIMEOUT": "The request timed out due to invalid parameters",
    "MSG_INVALID_CALLBACK_URL": "The provided callback URL is invalid",
    "MSG_VEHICLE_NOT_FOUND": "The specified vehicle could not be found",
    "MSG_VEHICLE_BUSY": "The vehicle is currently in use and cannot process the request",
    "MSG_NOT_CONFIGURED": "The system or vehicle is not properly configured",
    "MSG_TIMEOUT_EXPIRED": "The operation could not be completed as the timeout period expired",
    "MSG_TRANSMISSION_FAILED": "Data transmission failed during the operation",
    "MSG_SYS_ERROR": "A system error occurred while processing the request",
    "MSG_SYS_ERROR_COMUNICATION": "A critical communication error occurred",
    "MSG_NOT_FOUND": "The requested resource or endpoint could not be found",
    "MSG_ND": "The resource could not be found",
    "MSG_ADDRESS_GMAP_NOT_FOUND": "Address unknown",
  });
  
  newTranslationsMap.set('fr', {
    "title.win": "Données du véhicule plaque : {{plate}} vin : {{vin}}",
    "engine": "État du moteur",
    "doors": "État des portes",
    "cable": "État du câble",
    "fuelLevel": "Niveau de carburant",
    "chargeLevel": "Niveau de charge (SOC)",
    "ignition": "État du contact",
    "location": "Position du véhicule",
    "distanceTraveled": "Kilométrage total",
    "chargePort": "État du câble",
    "chargeStatus": "État de la charge",
    "maintenanceMode": "Mode maintenance",
    "bltUnlockDoors": "Déverrouiller les portes",
    "bltLockDoors": "Verrouiller les portes",
    "bltUnlockEngine": "Déverrouiller le moteur",
    "bltLockEngine": "Verrouiller le moteur",
    "bltRequestPosition": "Demander la position",
    "bltRequestStatus": "Demander l'état",
    "bltResetBoard": "Réinitialiser de bord",
    "bltRefresh": "Actualiser",
    "bltClose": "Fermer",
    "DIA_CONFIRM_OK": "Oui",
    "DIA_CONFIRM_TITLE": "Attention !",
    "DIA_CONFIRM_CANCEL": "Annuler",
    "DIA_CONFIRM_MSG": "Êtes-vous sûr de vouloir exécuter la commande \"{{param}}\" ?",
    "MSG_OK": "Opération réussie",
    "MSG_COMMAND_NOT_EXECUTED": "Commande non exécutée",
    "MSG_INVALID_TIMEOUT": "La demande a expiré en raison de paramètres invalides",
    "MSG_INVALID_CALLBACK_URL": "L'URL de rappel fournie est invalide",
    "MSG_VEHICLE_NOT_FOUND": "Le véhicule spécifié est introuvable",
    "MSG_VEHICLE_BUSY": "Le véhicule est actuellement utilisé et ne peut pas traiter la demande",
    "MSG_NOT_CONFIGURED": "Le système ou le véhicule n'est pas configuré correctement",
    "MSG_TIMEOUT_EXPIRED": "L'opération n'a pas pu être terminée car le délai d'attente est expiré",
    "MSG_TRANSMISSION_FAILED": "La transmission des données a échoué pendant l'opération",
    "MSG_SYS_ERROR": "Une erreur système s'est produite lors du traitement de la demande",
    "MSG_SYS_ERROR_COMUNICATION": "Une erreur critique de communication s'est produite",
    "MSG_NOT_FOUND": "La ressource ou l'endpoint demandé est introuvable",
    "MSG_ND": "La ressource est introuvable",
    "MSG_ADDRESS_GMAP_NOT_FOUND": "Address unknown",
  });
  
  
  languages.forEach(lang => {
    const existingTranslations = i18n.getResourceBundle(lang, 'translation');
    const newTranslations = {
      ...existingTranslations,
      ...{vehicle: {comunication: newTranslationsMap.get(lang)}}
    };
    i18n.addResourceBundle(lang, 'translation', newTranslations);
  });
  const { t } = useTranslation('', { keyPrefix: "vehicle.comunication" });
  return t;
};



class VehicleCommandRepoUtil {
    // Schema base per le proprietà (type, value, timestamp)
    private PropertySchema = Yup.object({
      type: Yup.string(),//.required('Type is required'),
      value: Yup.string(),//.required('Value is required'),
      timestamp: Yup.string()//.required('Timestamp is required').nullable()
    });
    
    public generateVehicleStatusDataSchema() {
      return       {
        vin: Yup.string(),//.required('VIN is required'),
        plate: Yup.string(),//.required('Plate is required'),
        timestamp: Yup.string(),//.required('Timestamp is required').nullable(),
        locationData: Yup.object({
          latitude: this.PropertySchema,
          longitude: this.PropertySchema,
          address: this.PropertySchema,
        }),
        statusData: Yup.object({
          engine: this.PropertySchema,
          doors: this.PropertySchema,
          fuelLevel: this.PropertySchema,
          chargeLevel: this.PropertySchema,
          cable: this.PropertySchema,
          chargePort: this.PropertySchema,
          chargeStatus: this.PropertySchema,
          maintenanceMode: this.PropertySchema,
          location: this.PropertySchema,
          distanceTraveled: this.PropertySchema,
          ignition: this.PropertySchema,
        }),
        statsData: Yup.object({
          totalDistanceTraveledInKm: this.PropertySchema,
        }),
      };
    }
    emptyVehicle: Vehicle = {
      vin: "",
      plate: "",
      timestamp: "",
      locationData: {
        latitude: { type: "N/A", value: "N/A", timestamp: "N/A" },
        longitude: { type: "N/A", value: "N/A", timestamp: "N/A" },
        address: { type: "N/A", value: "N/A", timestamp: "N/A" },
      },
      statusData: {
        engine: { type: "N/A", value: "N/A", timestamp: "N/A" },
        doors: { type: "N/A", value: "N/A", timestamp: "N/A" },
        fuelLevel: { type: "N/A", value: "N/A", timestamp: "N/A" },
        chargeLevel: { type: "N/A", value: "N/A", timestamp: "N/A" },
        cable: { type: "N/A", value: "N/A", timestamp: "N/A" },
        chargePort: { type: "N/A", value: "N/A", timestamp: "N/A" },
        chargeStatus: { type: "N/A", value: "N/A", timestamp: "N/A" },
        maintenanceMode: { type: "N/A", value: "N/A", timestamp: "N/A" },
        ignition: { type: "N/A", value: "N/A", timestamp: "N/A" },
        location: { type: "N/A", value: "N/A", timestamp: "N/A" },
        distanceTraveled: { type: "N/A", value: "N/A", timestamp: "N/A" },
      },
      statsData: {
        totalDistanceTraveledInKm: { type: "N/A", value: "N/A", timestamp: "N/A" },
      },
      imei: undefined,
      obuId: undefined,
    };
    
    // Funzione per generare una stringa casuale di lunghezza specificata
    public generateRandomString = (length:number): any => {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      return result;
    };
    public generateRandomStringByArray = (xarray:any): any => {
      return xarray[Math.floor(Math.random() * xarray.length)];
    };

    // Funzione per generare dati casuali per un singolo elemento
    public generateFakeData():object{
      let maps:any= {     
      'D': 'DIESEL',
      'E': 'ELECTRIC',
      'G': 'LPG',
      'B': 'GASOLINE',
      'HB': 'HYBRID GASOLINE',
      'HD': 'HYBRID DIESEL'
      }
      let vt:string = this.generateRandomStringByArray(['D','E', 'G', 'B', 'HB','HD']);
      
       return {
        id: Math.floor(Math.random() * 10000), // Genera un ID casuale
        vin: this.generateRandomStringByArray(["ZFAEFAC40NX059497"]),
        plate: this.generateRandomString(7),
        internalNumber: this.generateRandomString(12),
        series: Math.floor(Math.random() * 10).toString(), // Genera un numero di serie casuale
        motorizationCode: this.generateRandomString(2),
        motorizationDescr: this.generateRandomString(6),
        purchaseGroup: this.generateRandomString(10),
        registrationDate: new Date(Math.random() * (new Date().getTime())).toISOString(),
        realDeliveryDate: new Date(Math.random() * (new Date().getTime())).toISOString(),
        horsepower: Math.floor(Math.random() * 200), // Potenza del motore casuale
        fiscalHorsepower: Math.floor(Math.random() * 30),
        kw: Math.floor(Math.random() * 150),
        emissionCo2: Math.floor(Math.random() * 200),
        distance: Math.floor(Math.random() * 100000), // Distanza casuale
        termicTankCapacity: Math.floor(Math.random() * 80), // Capacità del serbatoio
        electricTankCapacity: Math.floor(Math.random() * 100),
        trunkCapacity: Math.floor(Math.random() * 600),
        tireSizeFront: this.generateRandomString(12),
        tireSizeRear: this.generateRandomString(12),
        currentTireType: this.generateRandomString(1),
        currentTireTypeDescr: 'Summer',
        tractionType: this.generateRandomString(1),
        tractionTypeDescr: 'FRONT-WHEEL DRIVE',
        colorExt: this.generateRandomString(25), // Genera un colore esterno casuale
        colorInt: this.generateRandomString(30), // Genera un colore interno casuale
        doorsNumber: Math.floor(Math.random() * 5) + 2, // Numero di porte casuale (da 2 a 6)
        ownerCode: this.generateRandomString(10),
        ownerDescr: this.generateRandomString(10),
        ownership: 'NOT_LEASYS',
        createDatetime: new Date().toISOString(),
        updateDatetime: new Date().toISOString(),
        fleetDays: Math.floor(Math.random() * 3000),
        fleetMinDate: new Date(Date.now() - Math.random() * 1000000000).toISOString(),
        fleetMaxDate: new Date(Date.now() - Math.random() * 1000000000).toISOString(),
        kms: Math.floor(Math.random() * 100000),
        connected: Math.random() < 0.5,
        loadIndex: (Math.random() * 2).toFixed(1),
        businessLine: this.generateRandomString(8),
        businessLineDescr: this.generateRandomString(15),
        jatoBatteryAmps: Math.floor(Math.random() * 100),
        jatoBatteryLifeExpecta: Math.floor(Math.random() * 100),
        jatoBatteryVoltage: Math.floor(Math.random() * 100),
        jatoDuration: Math.floor(Math.random() * 100),
        jatoRangeKm: Math.floor(Math.random() * 500),
        jatoWarrantyBatteryCode: "-",
        jatoWarrantyBatteryDescr: "NOT AVAILABLE",
        kmContract: Math.floor(Math.random() * 50000),
        mixedConsumption: (Math.random() * 10).toFixed(1),
        enginePowerCode: vt,// condizione se electrica = E
        enginePowerDescr: maps[vt],
        principalTireLoadCapacity: this.generateRandomString(2),
        principalTireSpeed: this.generateRandomString(1),
        rechargeStandardTime: "0.00",
        amps: 0,
        voltage: 0,
        vehicleStatus: "NOT_AVAILABLE",//condizione != 'ore'
        sapVehicleStatus: this.generateRandomString(1),
        vehicleType: this.generateRandomString(8),
        vehicleTypeDescr: "Car",
        importType: "SAP",
        referenceLocationId: this.generateRandomString(10),
        referenceLocationType: "LMS",
        referenceLocationDescription: "MILANO BOVISA",
        updateStatusReasonDatetime: new Date().toISOString(),
        firstFuelingDone: Math.random() < 0.5,
        reasonDescriptions: "Migrazione",
        keyless: this.generateRandomStringByArray([true,false]),
        reasonCodes: this.generateRandomStringByArray(["ORE2","PIPPO","MIG"]) // se sono super user + non contiene [ORE]
    }};
    
    


    public generateFakeArray = (numItems:number): any => {
      const fakeArray = [];
      for (let i = 0; i < numItems; i++) {
        fakeArray.push(this.generateFakeData());
      }
      return fakeArray;
    };

    // Esempio di utilizzo per generare un array di 10 elementi
    //const fakeDataArray = generateFakeArray(10);

}

export const vehicleCommandRepoUtil = new VehicleCommandRepoUtil()
